import { useEffect, useState } from 'react'
import { EVENTS } from '@sh/shared'
import type { ClientAction, PlayerView } from '@sh/shared'
import { ConfirmButton } from '../../components/Confirm.js'
import { request } from '../../net/socket.js'

/**
 * The host plays like everyone else and never sees another player's secret.
 * These are the only levers: unstick a dead phone, or end a game that has gone
 * wrong.
 *
 * It lives in the header as one word, and opens over the page rather than
 * sitting at the foot of it. Both of the things in here are answers to
 * something having gone wrong, which happens perhaps once an evening, and a
 * panel parked under the controls for the whole game was one more thing between
 * a player and the board - on a laptop, a whole row of the layout.
 */
export function HostTools({
  view,
  code,
  act,
}: {
  view: PlayerView
  code: string
  act: (a: ClientAction) => void
}) {
  const [open, setOpen] = useState(false)
  const [pin, setPin] = useState<{ name: string; value: string } | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (view.you?.isHost !== true) return null
  const offline = view.players.filter((p) => !p.connected)

  return (
    <>
      <button
        type="button"
        className="shell__host"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="host-tools-toggle"
      >
        host
        {/* The one thing the old summary line said that was worth saying from
            a closed panel: somebody's phone is not answering. */}
        {offline.length > 0 && (
          <span className="shell__host-count" aria-label={`${offline.length} offline`}>
            {offline.length}
          </span>
        )}
      </button>

      {open && (
        <div className="modal" data-testid="host-tools">
          <button
            type="button"
            className="modal__scrim"
            aria-label="Close host tools"
            onClick={() => setOpen(false)}
          />
          <div className="modal__card" role="dialog" aria-modal="true" aria-label="Host tools">
            <header className="modal__head">
              <h2 className="card__title">Host tools</h2>
              <button
                type="button"
                className="modal__close"
                aria-label="Close"
                onClick={() => setOpen(false)}
                data-testid="host-tools-close"
              >
                ✕
              </button>
            </header>

            <p className="card__hint">
              If someone’s phone has died, they open this site on any device and join{' '}
              <strong data-testid="host-tools-code">{code}</strong> under the same name — the game
              recognises the seat and asks them for a PIN. Issue it here and read it out. (They can
              also go straight to “Reclaim seat” if they would rather type the code themselves.)
            </p>
            <ul className="hosttools__list">
              {view.players.map((player) => (
                <li key={player.id} className="hosttools__row">
                  <span>
                    {player.name}
                    {!player.connected && <span className="badge badge--off">offline</span>}
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={async () => {
                      const ack = await request<{ pin: string }>(EVENTS.issuePin, {
                        targetId: player.id,
                      })
                      if (ack.ok) setPin({ name: player.name, value: ack.data.pin })
                    }}
                    data-testid={`issue-pin-${player.name}`}
                  >
                    claim PIN
                  </button>
                </li>
              ))}
            </ul>

            {pin && (
              <p className="hosttools__pin" data-testid="claim-pin">
                PIN for <strong>{pin.name}</strong>: <code>{pin.value}</code>
                <small>Valid once, for five minutes.</small>
              </p>
            )}

            {view.phase !== 'game_over' && view.phase !== 'lobby' && (
              <ConfirmButton
                className="btn--danger btn--block"
                confirmLabel="Really abandon this game?"
                // And it closes itself. This is the one control in here that
                // ends the thing everybody is looking at, and a panel left over
                // the result is a panel in the way of it. A claim PIN does the
                // opposite and has to stay up to be read out.
                onConfirm={() => {
                  act({ type: 'host_abort' })
                  setOpen(false)
                }}
                testId="abort-game"
              >
                Abandon the game
              </ConfirmButton>
            )}
          </div>
        </div>
      )}
    </>
  )
}
